/**
 * Required-field validator for the form-first intake (#222 D4, #214).
 *
 * Pure function — no React, no fetch — so it's trivial to unit test
 * and reusable across the IntakeReview form and (later) any inline
 * confirmation Berta does during the post-form narrative chat.
 *
 * The 5-field required floor:
 *   - first_name        — Berta's greeting + every personal mention
 *   - birth_year        — era anchoring (period-correct music/film,
 *                         reminiscence trivia, world-events context)
 *   - language          — STT/TTS hard requirement
 *   - 3+ interests      — drives [PLAY_MUSIC] / [SHOW_VIDEO] / news
 *                         briefings / activity proposals; without
 *                         these Berta has no media hooks. Counted
 *                         across the 8 structured-interest categories
 *                         (#214) — empty `interests` flat fallback
 *                         supported for legacy callers.
 *   - 1+ family members — relational anchors ("how is Pete?",
 *                         "when you and Maria…"); without these,
 *                         every conversation feels like meeting a
 *                         stranger
 *
 * Optional-but-encouraged: location, places lived, joyful memories,
 * blocked topics, friends, cognitive notes, additional family.
 *
 * Returns an array of {field, msg} — empty when all required fields
 * are present. Caller blocks save when array is non-empty.
 */

import INTAKE_REVIEW_STRINGS from '../i18n/intakeReviewStrings';

// #214 — keep in lockstep with FIELDS in app/services/structured_interests.py
// and STRUCTURED_INTEREST_FIELDS in web/src/pages/Portal.js.
// #711 — `career` removed (8 → 7); captured in the dedicated Working
// life section, not as an interest bucket.
const STRUCTURED_INTEREST_KEYS = [
  'music_artists',
  'music_genres',
  'sports_interests',
  'screen_interests',
  'places_interests',
  'hobbies',
  'other_interests',
];

/** Count non-empty entries across all 7 structured-interest categories.
 *  Falls back to the legacy flat `interests` array if structured is
 *  absent — keeps older callers (and any in-flight payloads) working. */
function countInterests(data) {
  const safe = data || {};
  const si = safe.structured_interests;
  if (si && typeof si === 'object') {
    let total = 0;
    for (const key of STRUCTURED_INTEREST_KEYS) {
      const arr = si[key];
      if (Array.isArray(arr)) {
        total += arr.filter(i => i && String(i).trim()).length;
      }
    }
    return total;
  }
  // Legacy flat array fallback
  const flat = safe.interests;
  if (Array.isArray(flat)) {
    return flat.filter(i => i && String(i).trim()).length;
  }
  return 0;
}

// #1107 — resolve the perspective-correct validation strings from the
// intakeReview namespace (or English fallback when no `t` is supplied).
function _validationMessages(t, perspective) {
  const v = (t && t.intakeReview && t.intakeReview.validation)
    || INTAKE_REVIEW_STRINGS.en.validation;
  const self = perspective === 'self';
  return {
    firstName: self ? v.firstNameSelf : v.firstNameFamily,
    birthdayMissing: self ? v.birthdayMissingSelf : v.birthdayMissingFamily,
    birthdayAdult: self ? v.birthdayAdultSelf : v.birthdayAdultFamily,
    language: self ? v.languageSelf : v.languageFamily,
    interests: self ? v.interestsSelf : v.interestsFamily,
    family: self ? v.familySelf : v.familyFamily,
  };
}

export function validateRequired(data, familyMembers, perspective = 'family', t = null) {
  const errors = [];
  const safeData = data || {};
  // #1107 — validation messages come from the intakeReview i18n namespace
  // so the banner follows the family's selected language; English fallback
  // when no `t` is supplied (e.g. unit tests).
  const msgs = _validationMessages(t, perspective);

  if (!safeData.first_name || !String(safeData.first_name).trim()) {
    errors.push({ field: 'first_name', msg: msgs.firstName });
  }

  // #734 — accept either the full birthday (ISO YYYY-MM-DD) or the
  // legacy year-only value. New intakes populate `birthday` via the
  // HTML date input; existing accounts may still have just `birth_year`
  // in extracted_data, which stays valid.
  const currentYear = new Date().getFullYear();
  const yr = parseInt(safeData.birth_year, 10);
  const bday = typeof safeData.birthday === 'string' ? safeData.birthday : '';
  const bdayYear = /^\d{4}-\d{2}-\d{2}$/.test(bday) ? parseInt(bday.slice(0, 4), 10) : null;
  const effectiveYear = bdayYear || yr;
  if (!effectiveYear || effectiveYear < 1900 || effectiveYear > currentYear) {
    errors.push({ field: 'birthday', msg: msgs.birthdayMissing });
  } else {
    // #741 — underage cutoff. Amigo Mio is a companion for elderly
    // users; under-18 entries are blocked at both family-perspective
    // (preventing a family from setting one up for a minor) and
    // self-perspective (preventing an underage user from signing
    // themselves up). 18 is the legal age of majority — strict
    // floor; not the elderly-user target band (we don't want to be
    // ageist about a 22-year-old signing up if one ever did, but
    // we do block minors). When full birthday is present, also
    // sanity-check via current date so a December birthday in
    // someone's 18th calendar year isn't accepted before the day.
    const today = new Date();
    let ageYears = today.getFullYear() - effectiveYear;
    if (/^\d{4}-\d{2}-\d{2}$/.test(bday)) {
      const bdayDate = new Date(`${bday}T00:00:00`);
      const monthDiff = today.getMonth() - bdayDate.getMonth();
      const dayDiff = today.getDate() - bdayDate.getDate();
      if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        ageYears -= 1;
      }
    }
    if (ageYears < 18) {
      errors.push({ field: 'birthday', msg: msgs.birthdayAdult });
    }
  }

  if (!safeData.language || !String(safeData.language).trim()) {
    errors.push({ field: 'language', msg: msgs.language });
  }

  const interestCount = countInterests(safeData);
  if (interestCount < 3) {
    errors.push({
      field: 'interests',
      msg: msgs.interests.replace('{count}', String(interestCount)),
    });
  }

  const fams = Array.isArray(familyMembers)
    ? familyMembers.filter(m => m && m.name && String(m.name).trim())
    : [];
  if (fams.length < 1) {
    errors.push({ field: 'family_members', msg: msgs.family });
  }

  return errors;
}

/**
 * Convenience: returns true when all required fields are filled.
 * Equivalent to validateRequired(...).length === 0.
 */
export function isFormComplete(data, familyMembers, perspective = 'family', t = null) {
  return validateRequired(data, familyMembers, perspective, t).length === 0;
}

export { STRUCTURED_INTEREST_KEYS };
